/// <reference lib="dom" />
import {
  GOAL_OPTIONS,
  PHONE_ERROR,
  buildPayload,
  fieldsFingerprint,
  mapContactResponse,
  newIdempotencyKey,
  validateFields,
  type FormFields,
  type SubmitOutcome,
} from "./contactForm.js";
import { PRIVACY_PATH, SMS_CONSENT_TEXT, TERMS_PATH } from "../shared/consentText.js";

// DOM bootstrap for the landing-page contact form. Composes the pure contactForm core with the
// static markup in index.html: renders the goal options, mounts the Cloudflare Turnstile widget
// (when a sitekey is configured), and drives submit -> POST /contact -> outcome UI. The token
// never persists anywhere; fetch is injected for tests.

const PHONE_HTML = '<a href="tel:+17135550182">(713) 555-0182</a>';
const PHONE_TEXT = "(713) 555-0182";

export interface TurnstileApi {
  render(container: Element, opts: Record<string, unknown>): string;
  reset(widgetId?: string): void;
  remove?(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __twOnload?: () => void;
  }
}

export type FunnelEvent = "cta_click" | "form_start" | "form_submit_ok" | "form_submit_err";

export interface ContactFormCtx {
  doc: Document;
  fetchFn: (url: string, init?: RequestInit) => Promise<{ status: number; json(): Promise<unknown> }>;
  turnstile?: TurnstileApi; // injected in tests; production lazily loads the CF script
  // Fire-and-forget funnel event sink; injected in tests. Production default uses
  // sendBeacon with a keepalive-fetch fallback. Failures are swallowed — the submit
  // path never awaits or depends on it (AC-3.2).
  sendEventFn?: (event: FunnelEvent) => void;
}

interface Els {
  form: HTMLFormElement;
  name: HTMLInputElement;
  email: HTMLInputElement;
  phone: HTMLInputElement;
  consent: HTMLInputElement;
  consentText: HTMLElement;
  phoneError: HTMLElement;
  goal: HTMLSelectElement;
  submit: HTMLButtonElement;
  status: HTMLElement;
  slot: HTMLElement;
  wrap: HTMLElement;
}

function findEls(doc: Document): Els | null {
  const form = doc.querySelector<HTMLFormElement>(".form-wrap form");
  const name = doc.getElementById("f-name") as HTMLInputElement | null;
  const email = doc.getElementById("f-email") as HTMLInputElement | null;
  const phone = doc.getElementById("f-phone") as HTMLInputElement | null;
  const consent = doc.getElementById("f-sms-consent") as HTMLInputElement | null;
  const consentText = doc.getElementById("f-sms-consent-text");
  const phoneError = doc.getElementById("f-phone-error");
  const goal = doc.getElementById("f-goal") as HTMLSelectElement | null;
  const submit = form?.querySelector<HTMLButtonElement>("button.submit") ?? null;
  const status = doc.getElementById("form-status");
  const slot = doc.getElementById("turnstile-slot");
  const wrap = doc.querySelector<HTMLElement>(".form-wrap");
  if (
    !form ||
    !name ||
    !email ||
    !phone ||
    !consent ||
    !consentText ||
    !phoneError ||
    !goal ||
    !submit ||
    !status ||
    !slot ||
    !wrap
  )
    return null;
  return { form, name, email, phone, consent, consentText, phoneError, goal, submit, status, slot, wrap };
}

// The consent label is rendered from the single source of truth (consentText.ts) so the DOM
// can never drift from the versioned copy the ConsentAudit records. Constant markup only.
function renderConsentText(els: Els, doc: Document): void {
  els.consentText.textContent = "";
  const parts = SMS_CONSENT_TEXT.split(/(Privacy Policy|Terms)/);
  for (const part of parts) {
    if (part === "Privacy Policy" || part === "Terms") {
      const a = doc.createElement("a");
      a.href = part === "Privacy Policy" ? PRIVACY_PATH : TERMS_PATH;
      a.textContent = part;
      els.consentText.appendChild(a);
    } else if (part) {
      els.consentText.appendChild(doc.createTextNode(part));
    }
  }
}

function defaultSendEvent(event: FunnelEvent): void {
  try {
    const body = JSON.stringify({ event });
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon("/event", body);
      return;
    }
    if (typeof fetch === "function") {
      void fetch("/event", { method: "POST", body, keepalive: true, headers: { "content-type": "application/json" } }).catch(
        () => {},
      );
    }
  } catch {
    // best-effort by design — instrumentation must never affect the form
  }
}

function renderGoalOptions(goal: HTMLSelectElement, doc: Document): void {
  goal.textContent = "";
  for (const opt of GOAL_OPTIONS) {
    const el = doc.createElement("option");
    el.value = opt.intent;
    el.textContent = opt.label;
    goal.appendChild(el);
  }
}

function setStatus(els: Els, message: string): void {
  els.status.textContent = message;
}

// Persist the idempotency key per content fingerprint across page reloads: a reload wipes
// module state, and a resubmit of IDENTICAL content with a fresh key would create a second
// lead (the server dedupes per key, not globally). sessionStorage scopes it to the tab.
const KEY_STORE = "contact-idem";

function loadStoredKey(fp: string): string | null {
  try {
    const raw = sessionStorage.getItem(KEY_STORE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { fp?: string; key?: string };
    return parsed.fp === fp && typeof parsed.key === "string" ? parsed.key : null;
  } catch {
    return null;
  }
}

function storeKey(fp: string, key: string): void {
  try {
    sessionStorage.setItem(KEY_STORE, JSON.stringify({ fp, key }));
  } catch {
    // storage unavailable (private mode etc.) — in-memory key still works for this page life
  }
}

function renderSuccess(els: Els, email: string, doc: Document): void {
  // Keep the EXISTING aria-live region in the DOM and announce through it — a live region
  // created in the same pass as its content is not reliably announced by screen readers.
  for (const child of Array.from(els.form.children)) {
    if (child !== els.status) child.remove();
  }
  const heading = els.wrap.querySelector("h2");
  if (heading) heading.textContent = "Thank you.";
  const promise = els.wrap.querySelector("p.promise");
  if (promise) promise.remove();
  els.status.className = "promise";
  // textContent only — never inject user input as HTML.
  els.status.textContent =
    `We got it. Watch ${email} for an email from us with the next step — ` +
    `a few quick questions so the right person calls you prepared.`;
  const alt = doc.createElement("p");
  alt.className = "alt";
  alt.innerHTML = `Rather talk now? ${PHONE_HTML}`; // constant markup, no user input
  els.form.appendChild(alt);
  // The submit button (previous focus) is gone; land focus on the confirmation.
  els.status.setAttribute("tabindex", "-1");
  els.status.focus();
}

export function initContactForm(ctx: ContactFormCtx): void {
  const els = findEls(ctx.doc);
  if (!els) return;

  renderGoalOptions(els.goal, ctx.doc);
  renderConsentText(els, ctx.doc);

  // Wrapped so even a throwing injected sink can never break the form (AC-3.2).
  const sink = ctx.sendEventFn ?? defaultSendEvent;
  const sendEvent = (event: FunnelEvent): void => {
    try {
      sink(event);
    } catch {
      // instrumentation is best-effort by design
    }
  };

  // Funnel instrumentation: hero/sticky CTA clicks and first field focus. Fire-and-forget.
  for (const cta of Array.from(ctx.doc.querySelectorAll<HTMLAnchorElement>('a.cta[href="#talk"]'))) {
    cta.addEventListener("click", () => sendEvent("cta_click"));
  }
  let formStarted = false;
  const markStart = () => {
    if (!formStarted) {
      formStarted = true;
      sendEvent("form_start");
    }
  };
  for (const field of [els.name, els.email, els.phone, els.goal]) {
    field.addEventListener("focus", markStart);
  }

  const sitekey = ctx.doc.querySelector<HTMLMetaElement>('meta[name="turnstile-sitekey"]')?.content ?? "";
  let token: string | undefined;
  let widgetId: string | undefined;
  let turnstileApi: TurnstileApi | undefined;

  const mountWidget = (api: TurnstileApi) => {
    turnstileApi = api;
    // No token yet -> a submit would fail server-side (fail-closed). Hold the button until
    // the widget hands us one; re-hold on expiry/error.
    els.submit.disabled = true;
    widgetId = api.render(els.slot, {
      sitekey,
      action: "contact",
      callback: (t: string) => {
        token = t;
        els.submit.disabled = false;
      },
      "expired-callback": () => {
        token = undefined;
        els.submit.disabled = true;
      },
      "error-callback": () => {
        token = undefined;
        els.submit.disabled = true;
        setStatus(els, `The verification widget hit a problem. Reload the page, or call ${PHONE_TEXT}.`);
      },
    });
  };

  if (!sitekey) {
    // Not configured yet: keep the page honest instead of collecting submissions that will 403.
    els.submit.disabled = true;
    setStatus(els, "The online form opens soon. Call us and we will take it from there.");
  } else if (ctx.turnstile) {
    mountWidget(ctx.turnstile);
  } else if (typeof window !== "undefined") {
    if (window.turnstile) {
      mountWidget(window.turnstile);
    } else {
      window.__twOnload = () => {
        if (window.turnstile) mountWidget(window.turnstile);
      };
      els.submit.disabled = true; // no widget until the script arrives
      const script = ctx.doc.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__twOnload&render=explicit";
      script.async = true;
      script.onerror = () => {
        setStatus(els, `The form cannot load its verification step right now. Please call ${PHONE_TEXT}.`);
      };
      ctx.doc.head.appendChild(script);
    }
  }

  let key = newIdempotencyKey();
  let keyFingerprint = "";
  let inFlight = false;

  const readFields = (): FormFields => ({
    name: els.name.value,
    email: els.email.value,
    phone: els.phone.value,
    intent: els.goal.value,
    smsConsent: els.consent.checked,
  });

  const submitOnce = async (fields: FormFields): Promise<SubmitOutcome> => {
    try {
      const res = await ctx.fetchFn("/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": key },
        body: JSON.stringify(buildPayload(fields, token)),
      });
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = undefined;
      }
      return mapContactResponse(res.status, body);
    } catch {
      return { kind: "network" };
    }
  };

  els.form.addEventListener("submit", (event: Event) => {
    event.preventDefault();
    if (inFlight) return;

    const fields = readFields();
    const errors = validateFields(fields);
    // Field-level phone error (exact copy per AC-1.1) rendered at the input; other errors
    // keep the existing status-line pattern. Cleared on the next attempt.
    const phoneErr = errors.find((e) => e.field === "phone");
    els.phoneError.textContent = phoneErr ? PHONE_ERROR : "";
    els.phone.setAttribute("aria-invalid", phoneErr ? "true" : "false");
    if (errors.length > 0) {
      setStatus(els, errors.map((e) => e.message).join(" "));
      return;
    }

    const fp = fieldsFingerprint(fields);
    if (fp !== keyFingerprint) {
      key = loadStoredKey(fp) ?? newIdempotencyKey();
      keyFingerprint = fp;
      storeKey(fp, key);
    }

    inFlight = true;
    els.submit.disabled = true;
    setStatus(els, "Sending…");

    void (async () => {
      let outcome = await submitOnce(fields);
      if (outcome.kind === "key-conflict") {
        key = newIdempotencyKey();
        storeKey(fieldsFingerprint(fields), key);
        outcome = await submitOnce(fields);
      }
      inFlight = false;

      if (outcome.kind === "success") {
        sendEvent("form_submit_ok"); // fire-and-forget, never awaited
        turnstileApi?.remove?.(widgetId); // detach cleanly before the widget node is removed
        renderSuccess(els, fields.email.trim(), ctx.doc);
        return;
      }
      sendEvent("form_submit_err");

      // Re-enable only for outcomes that did not consume/lose the token; the blocked and
      // retryable branches below hold the button until the widget mints a fresh token.
      if (outcome.kind === "invalid" || outcome.kind === "network") els.submit.disabled = false;
      if (outcome.kind === "invalid") {
        setStatus(
          els,
          outcome.errors.length > 0
            ? outcome.errors.map((e) => `${e.field}: ${e.message}`).join(" ")
            : "Something in the form needs fixing — please check and resend.",
        );
      } else if (outcome.kind === "blocked") {
        turnstileApi?.reset(widgetId);
        token = undefined;
        els.submit.disabled = true; // re-enabled by the widget's next token callback
        setStatus(els, "We could not verify the submission. Please try again, or just call us.");
      } else if (outcome.kind === "network") {
        setStatus(els, "Network problem — nothing was lost. Please try again.");
      } else {
        // Retryable server error: the server may already have consumed the Turnstile token
        // (verification happens before persistence), so a bare retry would 403. Reset the
        // widget so the retry carries a fresh token.
        turnstileApi?.reset(widgetId);
        token = undefined;
        els.submit.disabled = true; // re-enabled by the widget's next token callback
        setStatus(els, "Something went wrong on our side. Please try again in a minute, or call us.");
      }
    })();
  });
}

const autoForm = typeof document !== "undefined" ? document.querySelector(".form-wrap form") : null;
if (autoForm && typeof window !== "undefined") {
  initContactForm({
    doc: document,
    fetchFn: (url, init) => fetch(url, init),
  });
}
